// ScopeList: what the workspace's overview reaches, listed in the
// workspace's dock beside the chat (FleetMap lends the dock its tab, for as
// long as the overview is in front, and brings it in front at each click on
// the board): the sessions the board's search reaches (every one, until a
// click narrows it), and of them those review flagged, under where review
// left each. It follows the board as it narrows or widens. A row opens its
// session over the page, as the overview's Triage does.
//
// It reads what the board reads for the same search (the same keys), so
// listing it here costs little of its own: the flagged sessions as the
// board counts them, every one the search reaches past the match's cap, so
// its counts are the board's.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ScrollArea from "./ScrollArea.jsx";
import { NavSectionLabel } from "./ScopeSidebar.jsx";
import { Thread, dayGroup } from "./WorkspaceThreads.jsx";
import { countText } from "./WorkspaceAddOverlay.jsx";
import { useKeyedReads } from "../lib/keyedReads.js";
import { SESSION_MATCH_CAP } from "../lib/sessionMatch.js";
import { QUEUE_SECTIONS } from "../lib/fleetFindings.js";
import { previewTraceCanvas } from "../lib/traceCanvasStore.js";
import { useFlaggedInReach, useSessionMatch } from "../lib/workspaceSearch.js";

const PAGE = 80;
const NONE = [];
const NUM = new Intl.NumberFormat();
const LIST_SCROLL = Object.freeze({ overflow: { x: "hidden" } });

function Heading({ children }) {
  return (
    <div className="sticky top-0 z-[2] bg-[color:var(--bg)] pt-[6px]" role="presentation">
      <NavSectionLabel>{children}</NavSectionLabel>
    </div>
  );
}

// A row, drawn again only when what it shows changes: the dock's room
// changing (its seam dragged) draws none of them again.
const ScopeRow = memo(function ScopeRow({ item, onOpen }) {
  return <Thread item={item} onOpen={() => onOpen(item)} />;
});
const sameRow = (a, b) => {
  const keys = Object.keys(b);
  return keys.length === Object.keys(a).length && keys.every((k) => a[k] === b[k]);
};

function Quiet({ children }) {
  return <p className="m-0 px-[6px] py-[12px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{children}</p>;
}

// `q`: the search as the board reads it; `total`: how many sessions it
// reaches, as the board counts them (the list holds the newest of them past
// the match's cap), or null until counted; `onStart`: starts a workspace from
// the search.
function ScopeList({ range, q, total = null, onStart = null }) {
  const match = useSessionMatch(range, q);
  const matched = match.data?.sessions || NONE;
  // Every flagged session the search reaches, as the board counts them.
  const flaggedReach = useFlaggedInReach(range, q);
  const flags = flaggedReach.flags;
  // Not what the search found in each: the tab names the search already.
  const flaggedItems = flaggedReach.items;
  const sessionItems = useMemo(() => matched.map((s) => ({
    id: s.id, agent: s.agent, endpointId: s.endpointId, lastMs: s.lastMs, firstMs: s.firstMs,
  })), [matched]);
  const placeCounts = useMemo(() => {
    const by = new Map();
    for (const it of flaggedItems) by.set(it.place, (by.get(it.place) || 0) + 1);
    return by;
  }, [flaggedItems]);

  const triageRead = flaggedReach.status === "done";
  // What review flagged first, once there is any; the sessions otherwise.
  const [picked, setPicked] = useState(null);
  const tab = picked || (triageRead && !flaggedItems.length ? "sessions" : "triage");
  const items = tab === "triage" ? flaggedItems : sessionItems;
  const [limit, setLimit] = useState(PAGE);
  const listRef = useRef(null);
  const endRef = useRef(null);
  useEffect(() => { setLimit(PAGE); }, [q, tab]);
  // The list grows as its end comes into view.
  useEffect(() => {
    const el = endRef.current;
    if (!el || typeof IntersectionObserver === "undefined" || items.length <= limit) return undefined;
    const root = listRef.current?.closest("[data-overlayscrollbars-viewport]") || null;
    const io = new IntersectionObserver((seen) => { if (seen.some((s) => s.isIntersecting)) setLimit((n) => n + PAGE); }, { root, rootMargin: "240px" });
    io.observe(el);
    return () => io.disconnect();
  }, [items.length, limit]);
  const page = items.slice(0, limit);
  const heads = useKeyedReads("getSessionHeads", page.map((it) => it.id), { range, whole: tab === "sessions" && !!match.data?.prefiltered });
  // Each row keeps its object while what it shows holds.
  const kept = useRef(new Map());
  const keep = (row) => {
    const old = kept.current.get(row.id);
    if (old && sameRow(old, row)) return old;
    kept.current.set(row.id, row);
    return row;
  };
  if (kept.current.size > 4 * PAGE) kept.current.clear();
  const open = useCallback((item) => previewTraceCanvas({ id: item.id, title: item.title }), []);
  const rows = page.map((it) => {
    const head = heads.data?.[it.id] || null;
    const flag = flags.get(it.id);
    return {
      ...it,
      sev: it.sev ?? flag?.sev ?? null,
      findings: it.findings ?? flag?.findings ?? 0,
      title: it.title || head?.summary || `Session ${String(it.id).slice(0, 8)}`,
      agent: it.agent || head?.provider || "",
      endpointId: it.endpointId || head?.endpointId || "",
      lastMs: tab === "triage" ? it.lastMs ?? head?.lastMs : head?.lastMs ?? it.lastMs,
    };
  }).map(keep);
  const groups = [];
  for (const row of rows) {
    const section = tab === "triage" ? QUEUE_SECTIONS.find(([id]) => id === row.place) : null;
    const g = section ? { key: section[0], label: section[1], count: placeCounts.get(section[0]) } : dayGroup(row.lastMs);
    if (!groups.length || groups[groups.length - 1].key !== g.key) groups.push({ ...g, rows: [] });
    groups[groups.length - 1].rows.push(row);
  }

  const sessionsCount = total != null ? NUM.format(total) : match.data ? `${countText(matched.length)}${match.data.capped ? "+" : ""}` : null;
  const tabs = [
    { id: "triage", label: "Triage", count: triageRead ? NUM.format(flaggedItems.length) : null },
    { id: "sessions", label: "Sessions", count: sessionsCount },
  ];
  const loading = tab === "triage" ? flaggedReach.status === "loading" : match.loading && !match.data;
  const failed = tab === "triage" ? flaggedReach.status === "error" : !!match.error;
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]" data-scope-list="">
      <div className="flex h-[32px] min-w-0 shrink-0 items-center gap-x-[14px] overflow-hidden border-b border-[color:var(--line-soft)] px-[12px] text-[12px]" role="tablist" aria-label="What the click reached">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setPicked(t.id)}
            className={`whitespace-nowrap ${tab === t.id ? "font-medium text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
            data-scope-list-tab={t.id}
          >
            {t.label}
            {t.count ? <span className="ml-[5px] font-normal tabular-nums text-[color:var(--muted-soft)]">{t.count}</span> : null}
          </button>
        ))}
        {onStart ? (
          <button type="button" onClick={onStart} className="ml-auto min-w-0 truncate text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-scope-list-start="">
            Start a workspace
          </button>
        ) : null}
      </div>
      <ScrollArea className="min-h-0 flex-1" contentClassName="px-[6px] pb-[12px]" options={LIST_SCROLL}>
        <div ref={listRef} role="listbox" aria-label={tab === "triage" ? "Flagged sessions" : "Sessions"} data-scope-list-rows={tab}>
          {loading ? <Quiet>{tab === "triage" ? "Reading what review flagged..." : "Finding sessions..."}</Quiet>
            : failed ? <Quiet>The sessions could not be read.</Quiet>
              : !rows.length ? <Quiet>{tab === "triage" ? "Review flagged none of these sessions." : "No session here matches this."}</Quiet>
                : groups.map((g) => (
                  <div key={g.key} role="group" aria-label={g.label}>
                    <Heading>
                      {g.label}
                      {g.count != null ? <span className="ml-[6px] tabular-nums">{NUM.format(g.count)}</span> : null}
                    </Heading>
                    {g.rows.map((item) => (
                      <ScopeRow key={item.id} item={item} onOpen={open} />
                    ))}
                  </div>
                ))}
          {items.length > limit ? <div ref={endRef} className="h-[1px]" aria-hidden="true" /> : null}
          {tab === "sessions" && match.data?.capped && items.length <= limit && rows.length ? (
            <Quiet>{`These are the newest ${countText(SESSION_MATCH_CAP)} sessions that match. Narrow the search or the range to reach the rest.`}</Quiet>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}

// Drawn again only when what it lists changes, not as what holds it does.
export default memo(ScopeList);
